export const DESKTOP_TITLEBAR_HEIGHT = 40;

export type DesktopTitlebarCapability = {
  version: 1;
  left: number;
  right: number;
};

export function desktopNativeControlWidths(platform: string) {
  return platform === 'darwin'
    ? { left: 196, right: 0 }
    : { left: 120, right: 138 };
}

/** Older shells must keep the web header below their full-width native view. */
export function readDesktopTitlebarCapability(
  bridge: unknown
): DesktopTitlebarCapability | null {
  if (!bridge || typeof bridge !== 'object' || !('titlebarLayout' in bridge))
    return null;
  const value = bridge.titlebarLayout;
  if (
    !value ||
    typeof value !== 'object' ||
    !('version' in value) ||
    value.version !== 1 ||
    !('left' in value) ||
    !('right' in value) ||
    typeof value.left !== 'number' ||
    typeof value.right !== 'number' ||
    !(
      (value.left === 196 && value.right === 0) ||
      (value.left === 120 && value.right === 138)
    )
  )
    return null;
  return { version: 1, left: value.left, right: value.right };
}

export type DesktopTitlebarOverlay = {
  version: 1;
  left: number;
  right: number;
  height: number;
  subscribeFullscreen?(listener: (fullscreen: boolean) => void): () => void;
};

/** Full-bleed window: the web draws the top row around these native insets. */
export function desktopTitlebarOverlayInsets(platform: string) {
  return platform === 'darwin'
    ? { left: 76, right: 0, height: DESKTOP_TITLEBAR_HEIGHT }
    : { left: 0, right: 138, height: DESKTOP_TITLEBAR_HEIGHT };
}

/** Only shells that can drop their chrome strip advertise this capability. */
export function readDesktopTitlebarOverlay(
  bridge: unknown
): DesktopTitlebarOverlay | null {
  if (!bridge || typeof bridge !== 'object' || !('titlebarOverlay' in bridge))
    return null;
  const value: unknown = bridge.titlebarOverlay;
  if (!value || typeof value !== 'object') return null;
  const { version, left, right, height, subscribeFullscreen } = value as Record<
    string,
    unknown
  >;
  if (version !== 1) return null;
  const supported = ['darwin', 'win32'].some((platform) => {
    const insets = desktopTitlebarOverlayInsets(platform);
    return (
      left === insets.left && right === insets.right && height === insets.height
    );
  });
  if (!supported) return null;
  return {
    version: 1,
    left: left as number,
    right: right as number,
    height: height as number,
    ...(typeof subscribeFullscreen === 'function'
      ? {
          subscribeFullscreen: subscribeFullscreen as NonNullable<
            DesktopTitlebarOverlay['subscribeFullscreen']
          >,
        }
      : {}),
  };
}

/** Native views never overlap the interactive web header in merged mode. */
export function desktopWindowViewBounds(input: {
  width: number;
  height: number;
  platform: string;
  sidebarWidth: number;
  sidebarVisible: boolean;
  headerInTitlebar: boolean;
  overlayTitlebar?: boolean;
}) {
  const dimension = (value: number) =>
    Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  const width = dimension(input.width);
  const height = dimension(input.height);
  const barHeight = Math.min(DESKTOP_TITLEBAR_HEIGHT, height);
  const native = desktopNativeControlWidths(input.platform);
  const controlsWidth = Math.min(width, native.right);
  if (input.overlayTitlebar) {
    return {
      content: { x: 0, y: 0, width, height },
      chrome: { x: 0, y: 0, width: 0, height: 0 },
      controls: {
        x: width - controlsWidth,
        y: 0,
        width: controlsWidth,
        height: barHeight,
      },
      separateControls: controlsWidth > 0,
    };
  }
  const sidebarWidth = input.sidebarVisible ? dimension(input.sidebarWidth) : 0;
  const chromeWidth = input.headerInTitlebar
    ? Math.min(width - controlsWidth, Math.max(native.left, sidebarWidth))
    : width;
  const contentY = input.headerInTitlebar ? 0 : barHeight;
  return {
    content: { x: 0, y: contentY, width, height: height - contentY },
    chrome: { x: 0, y: 0, width: chromeWidth, height: barHeight },
    controls: {
      x: width - controlsWidth,
      y: 0,
      width: controlsWidth,
      height: barHeight,
    },
    separateControls: input.headerInTitlebar && controlsWidth > 0,
  };
}
